import type { AlertItem } from '../types/content'

/**
 * Contenu de la page d'accueil, repris du vrai site beaconsfield.ca.
 *
 * Les VISUELS sont des images de remplacement : les illustrations et photos de
 * Beaconsfield ne sont pas récupérables depuis leur site. À remplacer avant une démo.
 * Elles vivent ici, et non dans le JSX des composants, pour qu'une réécriture de
 * composant ne les efface pas.
 */
const PLACEHOLDER = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/d961d921-fc64-4172-a775-2646a67bcd2f.jpg',
  aerial: 'https://cdn.magicpatterns.com/patterns/generated-images/81733988-614c-4ea1-a92b-4d6bdcb10469.jpg',
  library: 'https://cdn.magicpatterns.com/patterns/generated-images/66b2a568-92b6-454c-9209-0204adb6a2b4.jpg',
  culture: 'https://cdn.magicpatterns.com/patterns/generated-images/7c3efef4-2477-4098-a5eb-04cd1fab915d.jpg',
  address: 'https://cdn.magicpatterns.com/patterns/generated-images/5c1bb1a0-98ab-4fff-a0e3-94c11eaefb42.jpg',
  seniors: 'https://cdn.magicpatterns.com/patterns/generated-images/0bf26428-32e7-41de-8bd9-514123997710.jpg',
  leaves: 'https://cdn.magicpatterns.com/patterns/generated-images/5fd42455-d112-4234-8bf8-10181a8b6838.jpg',
}

export interface HeroSlide {
  id: string
  title: string
  cta: string
  to: string
  image: string
}

/** Les 7 diapositives réelles du carrousel de beaconsfield.ca. */
export const heroSlides: HeroSlide[] = [
  {
    id: 'inscription',
    title: 'Nouvelle plateforme d’inscription',
    cta: 'En savoir plus',
    to: '/mes-loisirs',
    image: PLACEHOLDER.hero,
  },
  {
    id: 'urbanisme',
    title: 'Refonte du plan et des règlements de l’urbanisme',
    cta: 'En savoir plus',
    to: '/mes-permis',
    image: PLACEHOLDER.hero,
  },
  {
    id: 'centre-culturel',
    title: 'Campagne Centre culturel Beaconsfield',
    cta: 'En savoir plus',
    to: '/',
    image: PLACEHOLDER.culture,
  },
  {
    id: 'centre-recreatif',
    title: 'Rénovations du Centre récréatif',
    cta: 'En savoir plus',
    to: '/mes-loisirs',
    image: PLACEHOLDER.hero,
  },
  {
    id: 'alertes',
    title: 'Nouveau portail d’alertes',
    cta: 'En savoir plus',
    to: '/notifications',
    image: PLACEHOLDER.address,
  },
  {
    id: 'connectons',
    title: 'Projet Connectons Beaconsfield',
    cta: 'En savoir plus',
    to: '/mon-espace',
    image: PLACEHOLDER.aerial,
  },
  {
    id: 'bienvenue',
    title: 'Bienvenue sur le site de la ville de Beaconsfield',
    cta: 'Découvrir',
    to: '/',
    image: PLACEHOLDER.hero,
  },
]

/** Carte « Que recherchez-vous aujourd'hui ? » du hero, devenue le point d'entrée d'ATNA. */
export const heroSearch = {
  title: 'Que recherchez-vous aujourd’hui ?',
}

export const heroPrompts: { label: string; to: string }[] = [
  { label: 'Bac non ramassé', to: '/mes-collectes' },
  { label: 'Nid-de-poule', to: '/mes-demandes' },
  { label: 'Inscrire mon enfant', to: '/mes-loisirs' },
]

export type QuickAccessIcon =
  | 'smartphone'
  | 'megaphone'
  | 'book'
  | 'trash'
  | 'map'
  | 'briefcase'
  | 'ball'
  | 'permit'
  | 'alert'
  | 'pet'

export interface QuickAccessLink {
  id: string
  label: string
  icon: QuickAccessIcon
  to?: string
  href?: string
  /** Ouvre le dialogue d'inscription aux alertes quand la session est fermée. */
  action?: 'alertes'
  /** Entrée ajoutée par bciti+, absente du site actuel. */
  added?: boolean
}

/**
 * Le rail « Vos accès rapides » du site réel, où la barre de services bciti+ vient se
 * fondre. Les 7 liens d'origine sont conservés dans leur ordre ; six d'entre eux pointent
 * désormais vers un module. Trois entrées sont ajoutées.
 *
 * « Bibliothèque » mène à Ma carte : Beaconsfield tourne sur SirsiDynix Horizon, et la
 * carte citoyenne porte l'abonnement — c'est l'intégration documentée dans connectors.md.
 */
export const quickAccessLinks: QuickAccessLink[] = [
  { id: 'espace', label: 'Mon espace', icon: 'smartphone', to: '/mon-espace', added: true },
  { id: 'avis', label: 'Avis publics', icon: 'megaphone', to: '/notifications', action: 'alertes' },
  { id: 'biblio', label: 'Bibliothèque', icon: 'book', to: '/ma-carte' },
  { id: 'collectes', label: 'Calendrier des collectes', icon: 'trash', to: '/mes-collectes' },
  { id: 'carte', label: 'Carte interactive', icon: 'map', to: '/plan' },
  { id: 'emplois', label: 'Emplois', icon: 'briefcase', href: '#accueil' },
  { id: 'loisirs', label: 'Inscription en ligne', icon: 'ball', to: '/mes-loisirs' },
  { id: 'permis', label: 'Permis et certificats', icon: 'permit', to: '/mes-permis' },
  { id: 'demandes', label: 'Requêtes et signalements', icon: 'alert', to: '/mes-demandes', added: true },
  { id: 'animaux', label: 'Médailles animaux', icon: 'pet', to: '/mes-animaux', added: true },
]

export interface HomeNewsItem {
  id: string
  category: string
  date: string
  title: string
  to: string
}

export const newsSection = {
  title: 'Quoi de neuf?',
  cta: { label: 'Toutes les actualités', to: '/notifications' },
  background: PLACEHOLDER.aerial,
  items: [
    {
      id: 'sondage',
      category: 'Administration',
      date: '2 septembre 2026',
      title: 'Sondage sur les communications de la Ville',
      to: '/notifications',
    },
    {
      id: 'depot-feuilles',
      category: 'Environnement',
      date: '2 octobre 2026',
      title: 'Ouverture du dépôt à feuilles',
      to: '/mes-collectes',
    },
  ] as HomeNewsItem[],
}

export interface HomeEventItem {
  id: string
  category: string
  title: string
  date: string
  detail: string
  place: string
  image?: string
  to: string
}

export const eventsSection = {
  eyebrow: 'Prochainement',
  title: 'Calendrier des événements',
  cta: { label: 'Tous les événements', to: '/' },
  items: [
    {
      id: 'trousse',
      category: 'Activités pour enfants',
      title: 'Trousse à emporter : l’Action de grâce',
      date: '6 octobre 2026',
      detail: 'Disponibles au comptoir du prêt à partir du 6 octobre à 10 h',
      place: 'Bibliothèque',
      image: PLACEHOLDER.library,
      to: '/',
    },
    {
      id: 'conversation',
      category: 'Ateliers et conférences',
      title: 'L’heure de conversation : français',
      date: '6 octobre 2026',
      detail: 'Places limitées, inscription au comptoir',
      place: 'Bibliothèque',
      to: '/',
    },
  ] as HomeEventItem[],
}

export interface DiscoverCardItem {
  id: string
  label: string
  title: string
  image: string
  to?: string
  href?: string
}

export const discoverCards: DiscoverCardItem[] = [
  {
    id: 'centre-culturel',
    label: 'Nouveau',
    title: 'Centre culturel',
    image: PLACEHOLDER.culture,
    href: 'https://www.culturebeaconsfield.ca/',
  },
  {
    id: 'adresse',
    label: 'Outil de recherche',
    title: 'Recherche par adresse',
    image: PLACEHOLDER.address,
    to: '/plan',
  },
  {
    id: 'aines',
    label: 'Ressources',
    title: 'Services aux aînés',
    image: PLACEHOLDER.seniors,
    to: '/mes-loisirs',
  },
]

/** Le bandeau que Beaconsfield affiche déjà : bciti+ est la réponse à sa promesse. */
export const promoBanner = {
  title: 'Tout au même endroit, bien plus simple',
  cta: { label: 'Votre portail citoyen', to: '/mon-espace' },
}

export const footerSubscribe = {
  eyebrow: 'Abonnements',
  title: 'Pour ne rien manquer, inscrivez-vous à nos différents services',
  cta: { label: 'S’inscrire', to: '/notifications/parametres' },
}

export const alert: AlertItem = {
  level: 'info',
  title: 'Ouverture du dépôt à feuilles',
  detail: 'Site de dépôt du 300, Beaurepaire — ouvert du lundi au vendredi.',
  body: [
    'Le dépôt à feuilles du 300, Beaurepaire est ouvert aux résidents de Beaconsfield. Les feuilles doivent être apportées en vrac ou en sacs de papier — les sacs de plastique sont refusés.',
    'Heures d’ouverture : du lundi au vendredi, de 7 h 15 à 11 h 30 et de 12 h 15 à 15 h. Une preuve de résidence peut être demandée à l’entrée.',
  ],
  scope: 'Site de dépôt : 300, Beaurepaire · À compter du 2 octobre 2026',
}
